import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, PlusCircle, TrendingUp, User as UserIcon, CircleUserRound, Circle,
  Bell, Settings as SettingsIcon, Gamepad2,
  ShieldCheck, Megaphone, HelpCircle, LogOut, Users,
} from 'lucide-react';
import { LogoIcon } from '@/components/Logo';
import { NAV_HEIGHT_PX, BOTTOM_SAFE_AREA } from '@/constants/nav';
import { isAdmin as isAdminByEmail } from '@/lib/adminAuth';
import { isNativePlatform } from '@/lib/appleNative';

void LogoIcon;

// Admin access — true when the cached user has the `is_admin` flag set OR
// when their email matches the `ADMIN_EMAILS` allowlist in `lib/adminAuth.js`
// (which mirrors backend `ADMIN_EMAILS` in `server.py`).  The allowlist
// path lets admins log in for the first time and immediately see the
// admin shortcut in the profile sheet without needing a server-side
// `role` migration on their user document.
const isAdmin = () => {
  try {
    if (JSON.parse(localStorage.getItem('zenscroll_user') || '{}')?.is_admin === true) {
      return true;
    }
  } catch { /* fall through */ }
  return isAdminByEmail();
};

/**
 * Mobile-only fixed bottom navigation.
 *
 * - Hidden on >= md (the desktop landing/marketing Nav owns navigation there).
 * - Renders only when the user has a `token`. Marketing & auth routes don't get
 *   a tab bar so they stay focused on conversion.
 * - Respects iOS / Android safe-area-inset-bottom via Tailwind's
 *   `pb-[env(safe-area-inset-bottom)]` so it never sits under the home indicator.
 * - Adds `pb-24 md:pb-0` style hooks (callers can opt in); we don't force
 *   global padding here so we don't break pages that already manage their own.
 */
const TABS = [
  { id: 'home',      label: 'Home',      path: '/',         Icon: Home },
  { id: 'zenflow',   label: 'Zenflow',   path: '/zenflow',  Icon: Circle },
  { id: 'create',    label: 'Create',    path: '/create',   Icon: PlusCircle },
  { id: 'play',      label: 'Play',      path: '/play',     Icon: Gamepad2 },
  // "Community" was previously labelled "Connect" — same route (`/connect`
  // also aliases `/discover`), refreshed label + Users icon to match the
  // Feb-2026 bottom-nav visual spec.
  { id: 'community', label: 'Community', path: '/connect',  Icon: Users },
  // "Explore" was previously labelled "Trending" — same route (`/trending`
  // also aliases `/prompts`), relabelled to match the visual spec.
  { id: 'explore',   label: 'Explore',   path: '/trending', Icon: TrendingUp },
  { id: 'profile',   label: 'Profile',   path: '/profile',  Icon: CircleUserRound },
];

// Routes where the bottom nav should NOT render even when token is present.
// `/`, `/welcome`, `/home` are intentionally NOT hidden anymore — Home is now
// a primary tab so the bar must be visible there.
const HIDDEN_ROUTES = new Set([
  '/auth',
  '/auth/apple',
  '/auth/callback',
  '/auth/success',
  '/auth/apple-callback',
  '/onboarding',
  '/splash',
]);

export default function MobileBottomNav({ token }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Show on every in-app route (including marketing / landing) — the tabs
  // themselves route users to auth flows when they tap into a signed-in
  // destination. Only hidden on the routes explicitly reserved for
  // full-screen flows (splash, auth, admin, legal pages).
  const shouldRender = (() => {
    if (HIDDEN_ROUTES.has(pathname)) return false;
    if (
      pathname.startsWith('/auth') ||
      pathname.startsWith('/admin') ||
      pathname === '/privacy' ||
      pathname === '/terms' ||
      pathname === '/delete-account' ||
      pathname === '/child-safety'
    ) {
      return false;
    }
    return true;
  })();

  // Native builds (iOS/Android wrapper) keep the tab bar at EVERY width —
  // on iPad the web-only hamburger and the <lg top-nav links are unavailable,
  // so without this the native tablet app had no route to Profile/Settings
  // (Apple review finding, Aug 2026). Web keeps the original `md:hidden`.
  const native = isNativePlatform();

  // Reserve bottom padding on <body> so page content isn't hidden behind the
  // fixed nav. Only applies at <md (where the nav actually renders) — we set
  // the class on documentElement and rely on a CSS rule. Native builds add a
  // second, viewport-independent class so iPad widths get the padding too.
  useEffect(() => {
    const cls = 'has-mobile-bottom-nav';
    const nativeCls = 'native-has-bottom-nav';
    if (shouldRender) {
      document.documentElement.classList.add(cls);
      if (native) document.documentElement.classList.add(nativeCls);
    } else {
      document.documentElement.classList.remove(cls);
      document.documentElement.classList.remove(nativeCls);
    }
    return () => {
      document.documentElement.classList.remove(cls);
      document.documentElement.classList.remove(nativeCls);
    };
  }, [shouldRender, native]);

  const [profileSheetOpen, setProfileSheetOpen] = useState(false);
  if (!shouldRender) return null;

  const isActive = (path) => {
    if (path === '/') return pathname === '/' || pathname === '/welcome' || pathname === '/home';
    if (path === '/zenflow') return pathname === '/zenflow' || pathname.startsWith('/zenflow/');
    if (path === '/create') return pathname === '/create' || pathname.startsWith('/create/');
    if (path === '/play') return pathname === '/play' || pathname.startsWith('/play/');
    if (path === '/connect') return pathname === '/connect' || pathname === '/discover' || pathname.startsWith('/connect/');
    if (path === '/trending') return pathname === '/trending' || pathname === '/prompts' || pathname === '/explore';
    if (path === '/profile') return pathname === '/profile' || pathname.startsWith('/profile/') || pathname === '/settings' || pathname === '/notifications' || pathname.startsWith('/admin') || pathname === '/help' || pathname === '/about';
    return false;
  };

  const handleTabPress = (id, path) => {
    // Signed-out visitors bounce to /auth when they try to enter an
    // authenticated destination (Create posts, Profile). Home / Zenflow /
    // Community / Explore have public reads so we let them through and the
    // page's own auth gates handle any protected actions.
    if (!token && (id === 'profile' || id === 'create')) {
      navigate(`/auth?next=${encodeURIComponent(path)}`);
      return;
    }
    // Profile tab: navigate directly to the user's profile page.  The user
    // can access Settings via the gear icon and Sign Out via the logout
    // icon in the Profile page's top-right action row.  No more sheet.
    if (id === 'profile') {
      let username = null;
      try { username = JSON.parse(localStorage.getItem('zenscroll_user') || 'null')?.username; }
      catch { /* ignore */ }
      navigate(username ? `/profile/${username}` : '/profile/me');
      return;
    }
    navigate(path);
  };

  const goFromSheet = (path) => {
    setProfileSheetOpen(false);
    navigate(path);
  };

  const handleSignOut = () => {
    try {
      localStorage.removeItem('zenscroll_token');
      localStorage.removeItem('zenscroll_user');
    } catch { /* ignore */ }
    setProfileSheetOpen(false);
    navigate('/auth');
  };

  return (
    <nav
      data-testid="mobile-bottom-nav"
      aria-label="Primary"
      // Solid `#0A1514` so the iOS home-indicator safe area filler below
      // the tab row inherits the same opaque colour — no visible gap
      // between the bar and the bottom edge on devices with notches.
      //
      // IMPORTANT: the safe-area inset is rendered as a SIBLING `<div>`
      // below `<ul>` (see below) — NOT as `padding-bottom` on this <nav>.
      // Otherwise iOS centres icons against the nav's *total* height
      // (52 px row + 34 px home indicator) and the icons appear to drift
      // downward / look clipped on iPhone 14/15 Pro Max.  With the
      // sibling-filler layout the icons are centred strictly inside the
      // 52 px row, and the filler is decorative background only.
      className={`${native ? '' : 'md:hidden'} fixed inset-x-0 bottom-0 z-[100] bg-[#0A1514] border-t border-white/10 mobile-chrome-bottom`}
    >
      <ul className="grid grid-cols-7" style={{ height: `${NAV_HEIGHT_PX}px` }}>
        {TABS.map(({ id, label, path, Icon }) => {
          const active = isActive(path);
          return (
            <li key={id} className="flex">
              <button
                type="button"
                data-testid={`mobile-tab-${id}`}
                aria-label={label}
                aria-current={active ? 'page' : undefined}
                onClick={() => handleTabPress(id, path)}
                // `relative` so the active-tab top indicator can be absolutely
                // positioned inside the button.  Column-centred so the icon
                // and label sit together in the middle of the 62 px row.
                className={`relative flex-1 h-full flex flex-col items-center justify-center gap-1 transition-colors ${
                  active
                    ? id === 'zenflow'
                      ? 'text-[#5EEAD4]'
                      : 'text-white'
                    : 'text-[#7B8794] hover:text-white/85'
                }`}
              >
                <Icon
                  className={`w-6 h-6 transition-transform ${active ? 'scale-110' : ''}`}
                  strokeWidth={active ? 2.25 : 1.75}
                />
                <span
                  className={`text-[10px] leading-none tracking-wide ${
                    active ? 'font-semibold' : 'font-medium'
                  }`}
                >
                  {label}
                </span>
                {active && (
                  <span
                    aria-hidden="true"
                    className="absolute top-0 h-[2px] w-8 rounded-full bg-[#5EEAD4]"
                  />
                )}
              </button>
            </li>
          );
        })}
      </ul>
      {/* Safe-area filler — background-only, sits below the 52 px tab row
          so the home indicator on iPhone has solid #0A1514 underneath it.
          `aria-hidden` because there's nothing interactive here, and it's
          a sibling of <ul> (not a padding on <nav>) so the tab icons stay
          centred strictly inside the 52 px row above. */}
      <div
        aria-hidden="true"
        data-testid="mobile-bottom-nav-safe-area"
        style={{ height: BOTTOM_SAFE_AREA }}
      />
      <ProfileSheet
        open={profileSheetOpen}
        onClose={() => setProfileSheetOpen(false)}
        go={goFromSheet}
        onSignOut={handleSignOut}
      />
    </nav>
  );
}

const SHEET_ITEMS = [
  { id: 'profile',       label: 'Profile',         path: '/profile',         Icon: UserIcon },
  { id: 'following',     label: 'Following Feed',  path: '/following',       Icon: Users },
  { id: 'settings',      label: 'Settings',        path: '/settings',        Icon: SettingsIcon },
  { id: 'notifications', label: 'Notifications',   path: '/notifications',   Icon: Bell },
  { id: 'admin',         label: 'Admin Dashboard', path: '/admin',           Icon: ShieldCheck, adminOnly: true },
  { id: 'ads',           label: 'Ads Dashboard',   path: '/admin/ads',       Icon: Megaphone,   adminOnly: true },
  { id: 'moderation',    label: 'Moderation',      path: '/admin/moderation',Icon: ShieldCheck, adminOnly: true },
  { id: 'help',          label: 'Help & FAQ',      path: '/help',            Icon: HelpCircle },
];

function ProfileSheet({ open, onClose, go, onSignOut }) {
  const admin = isAdmin();
  const items = SHEET_ITEMS.filter((it) => !it.adminOnly || admin);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="profile-sheet-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="md:hidden fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            data-testid="profile-sheet"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'tween', duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="absolute inset-x-0 bottom-0 bg-[#0A1514] border-t border-white/10 rounded-t-3xl pt-2"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
          >
            <div className="mx-auto w-10 h-1.5 rounded-full bg-white/20 mb-3" />
            <ul className="px-2 pb-2">
              {items.map(({ id, label, path, Icon }) => (
                <li key={id}>
                  <button
                    type="button"
                    data-testid={`profile-sheet-${id}`}
                    onClick={() => go(path)}
                    className="w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl hover:bg-white/5 active:bg-white/10 transition-colors text-left"
                  >
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#1C4749]/15 text-[#1C4749]">
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="text-base text-white">{label}</span>
                  </button>
                </li>
              ))}
              <li className="pt-2 mt-2 border-t border-white/5">
                <button
                  type="button"
                  data-testid="profile-sheet-signout"
                  onClick={onSignOut}
                  className="w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl hover:bg-red-500/10 active:bg-red-500/15 transition-colors text-left"
                >
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-red-500/15 text-red-400">
                    <LogOut className="w-4 h-4" />
                  </span>
                  <span className="text-base text-red-400">Sign out</span>
                </button>
              </li>
            </ul>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
